import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload } from 'lucide-react';
import { createProduct, uploadImages } from '../../utils/api';
import { useAuth } from '../../components/auth/AuthContext';

interface ProductFormData {
  title: string;
  club: string;
  category: string;
  product_type: string;
  size: string;
  price: string;
  description: string;
  images: File[];
}

interface UploadedImage {
  url: string;
  original_name: string;
  saved_name: string;
}

const CreateProduct = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<ProductFormData>({
    title: '',
    club: '',
    category: '',
    product_type: 'Sonstiges',
    size: '',
    price: '',
    description: '',
    images: []
  });
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const previewUrlsRef = useRef<string[]>([]);

  // Ref aktuell halten für Cleanup beim Unmount
  useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);

  // Blob-URLs beim Unmount freigeben
  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="text-center py-12 text-gray-500">Lädt...</div>;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setFormData({ ...formData, images: files });

      const urls = files.map(file => URL.createObjectURL(file));
      setPreviewUrls(prevUrls => {
        prevUrls.forEach(url => URL.revokeObjectURL(url));
        return urls;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!formData.title || !formData.price || !formData.category || !formData.size) {
        setError('Bitte fülle alle erforderlichen Felder aus.');
        return;
      }

      const price = parseFloat(formData.price);
      if (isNaN(price) || price <= 0) {
        setError('Bitte gib einen gültigen Preis ein.');
        return;
      }

      let imageUrl = '';
      let additionalImages: string[] = [];

      if (formData.images.length > 0) {
        const uploadResult = await uploadImages(formData.images);
        if (!uploadResult.success) {
          throw new Error(uploadResult.message || 'Fehler beim Bildupload');
        }
        if (uploadResult.data && uploadResult.data.length > 0) {
          imageUrl = uploadResult.data[0].url;
          additionalImages = uploadResult.data.slice(1).map((img: UploadedImage) => img.url);
        }
      }

      const response = await createProduct({
        title: formData.title,
        description: formData.description,
        price,
        category: formData.category,
        product_type: formData.product_type,
        size: formData.size,
        image_url: imageUrl,
        additional_images: additionalImages
      });

      if (response.success) {
        navigate('/my-products');
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err instanceof Error ? 'Ein Fehler ist aufgetreten: ' + err.message : 'Ein unbekannter Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Produkt verkaufen</h1>

        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Bilder Upload */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">Produktbilder</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                id="images"
                onChange={handleImageUpload}
              />
              <label
                htmlFor="images"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <Upload size={32} className="text-gray-400 mb-2" />
                <span className="text-gray-500">Klicke zum Hochladen oder ziehe Bilder hierher</span>
              </label>

              {previewUrls.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {previewUrls.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`Vorschau ${index + 1}`}
                      className="w-full h-32 object-cover rounded"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Titel */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="title">Titel</label>
            <input
              type="text"
              id="title"
              name="title"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Kategorie */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="category">Kategorie</label>
            <select
              id="category"
              name="category"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              value={formData.category}
              onChange={handleInputChange}
              required
            >
              <option value="">Kategorie wählen</option>
              <option value="Männer">Männer</option>
              <option value="Frauen">Frauen</option>
              <option value="Jungen">Jungen</option>
              <option value="Mädchen">Mädchen</option>
              <option value="Equipment">Equipment</option>
            </select>
          </div>

          {/* Produktart */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="product_type">Produktart</label>
            <select
              id="product_type"
              name="product_type"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              value={formData.product_type}
              onChange={handleInputChange}
            >
              <option value="Sonstiges">Sonstiges</option>
              <option value="Schuhe">Schuhe</option>
              <option value="Trikot">Trikot / Jersey</option>
              <option value="Hose">Hose / Short</option>
              <option value="Pullover">Pullover / Hoodie</option>
              <option value="Jacke">Jacke</option>
              <option value="Tasche">Tasche / Rucksack</option>
              <option value="Ball">Ball</option>
            </select>
          </div>

          {/* Größe */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="size">Größe</label>
            <input
              type="text"
              id="size"
              name="size"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              value={formData.size}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Preis */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="price">Preis (€)</label>
            <input
              type="number"
              step="0.01"
              id="price"
              name="price"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              value={formData.price}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Beschreibung */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2" htmlFor="description">Beschreibung</label>
            <textarea
              id="description"
              name="description"
              rows={4}
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              value={formData.description}
              onChange={handleInputChange}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-orange-500 text-white py-3 px-4 rounded hover:bg-orange-600 transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Wird erstellt...' : 'Produkt erstellen'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateProduct;
